/**
 * The storefront's copy of the listing-description whitelist (api#211).
 *
 * The api is the trust boundary: it stores a description only within this list
 * (api `src/libs/helpers/sanitize-listing-description.helper.ts`). The storefront applies the same
 * list once more before it renders the description as HTML, so a row the api never cleaned — or a
 * future api regression — still cannot run anything in a buyer's browser.
 *
 * It is exactly what the dealer cabinet's editor writes (sales `src/components/editor/editor.tsx`,
 * tiptap 3). The two copies must stay identical: `SafeHtml.spec.tsx` pins this one as literals, so a
 * change here without the api (or the other way round) is a visible diff in both repositories.
 */

const HEADINGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

export const ALLOWED_TAGS = [
  'p',
  'br',
  ...HEADINGS,
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'a',
  'ul',
  'ol',
  'li',
  'blockquote',
  'pre',
  'code',
  'hr',
  'img',
];

/** Attributes per tag; a tag that is not listed keeps none. */
export const ATTRIBUTES_BY_TAG: Record<string, string[]> = {
  a: ['href', 'rel', 'target', 'class'],
  img: ['src', 'alt', 'class'],
  p: ['style'],
  ...Object.fromEntries(HEADINGS.map((tag) => [tag, ['style', 'class']])),
  ul: ['class'],
  ol: ['class'],
  li: ['class'],
  blockquote: ['class'],
  hr: ['class'],
  pre: ['class'],
  code: ['class'],
};

/** `minimal` is the dashboard theme's class prefix (sales `src/theme/theme-config.ts`). */
export const EDITOR_CLASS = /^minimal__editor__content__[A-Za-z_]+$/;

/** The language CodeBlockLowlight writes on the `code` inside `pre`. */
export const LANGUAGE_CLASS = /^language-[a-z0-9-]+$/;

/**
 * The api's rule: one `text-align` declaration with an allowed value and nothing after it (no
 * `!important`); it is kept on its own and everything else in `style` is dropped.
 */
export const ALIGN_DECLARATION = /(?:^|;)\s*text-align\s*:\s*(left|center|right|justify)\s*(?:;|$)/;

export const LINK_HREF = /^(https?|mailto):/i;
export const IMAGE_SRC = /^https?:/i;

/** What tiptap's Link writes on every link. */
export const LINK_REL = 'noopener noreferrer nofollow';
export const LINK_TARGET = '_blank';
